/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FiShield, FiPlus, FiTrash2, FiCheck, FiX, FiAlertCircle,
  FiKey, FiSmartphone, FiLock
} from 'react-icons/fi';
import secureStorage from '../services/secureStorage';
import './WebAuthnManager.css';

// Helper to convert ArrayBuffer to Base64URL
const arrayBufferToBase64URL = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(byte => binary += String.fromCharCode(byte));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Helper to convert Base64URL to ArrayBuffer
const base64URLToArrayBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const WebAuthnManager = ({ onClose }) => {
  const { t } = useTranslation();
  const [securityKeys, setSecurityKeys] = useState([]);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [isSupported, setIsSupported] = useState(false);

  // Check WebAuthn support
  useEffect(() => {
    const checkSupport = async () => {
      const supported = window.PublicKeyCredential !== undefined;
      setIsSupported(supported);
      
      if (supported) {
        // Check for platform authenticator (Windows Hello, Touch ID, etc.)
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          console.log('Platform authenticator available:', available);
        } catch (e) {
          console.log('Platform authenticator check failed:', e);
        }
      }
    };
    checkSupport();
  }, []);

  // Load saved security keys
  useEffect(() => {
    const loadKeys = async () => {
      try {
        if (secureStorage.isReady()) {
          const cached = await secureStorage.getItem('webauthn_keys');
          if (Array.isArray(cached)) {
            setSecurityKeys(cached);
            return;
          }
        }
      } catch (e) {
        console.warn('Error loading security keys cache:', e);
      }

      const saved = localStorage.getItem('securityKeys');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setSecurityKeys(parsed);
          if (secureStorage.isReady()) {
            await secureStorage.setItem('webauthn_keys', parsed);
            localStorage.removeItem('securityKeys');
          }
        } catch (e) {
          console.error('Error loading security keys:', e);
        }
      }
    };

    loadKeys();
  }, []);

  // Save security keys
  const saveKeys = async (keys) => {
    setSecurityKeys(keys);
    if (secureStorage.isReady()) {
      await secureStorage.setItem('webauthn_keys', keys);
    } else {
      localStorage.setItem('securityKeys', JSON.stringify(keys));
    }
  };

  // Generate random bytes
  const generateRandomBytes = (length) => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return array;
  };

  // Register a new security key
  const registerSecurityKey = async () => {
    if (!keyName.trim()) {
      setError(t('webauthn.keyNameRequired'));
      return;
    }

    setRegistering(true);
    setError(null);

    try {
      const userId = generateRandomBytes(32);
      const challenge = generateRandomBytes(32);

      const publicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: 'RFID Password Manager',
          id: window.location.hostname
        },
        user: {
          id: userId,
          name: localStorage.getItem('rfidId') || 'user@rfid-vault',
          displayName: keyName
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },   // ES256
          { type: 'public-key', alg: -257 }  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'cross-platform', // For USB keys like YubiKey
          userVerification: 'preferred',
          residentKey: 'preferred'
        },
        timeout: 60000,
        attestation: 'direct'
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (credential) {
        const newKey = {
          id: arrayBufferToBase64URL(credential.rawId),
          name: keyName,
          type: credential.authenticatorAttachment || 'cross-platform',
          createdAt: new Date().toISOString(),
          publicKey: arrayBufferToBase64URL(credential.response.getPublicKey ? 
            credential.response.getPublicKey() : 
            credential.response.attestationObject)
        };

        saveKeys([...securityKeys, newKey]);
        setSuccess(t('webauthn.keyRegistered'));
        setShowRegisterModal(false);
        setKeyName('');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (e) {
      console.error('WebAuthn registration error:', e);
      if (e.name === 'NotAllowedError') {
        setError(t('webauthn.cancelled'));
      } else if (e.name === 'InvalidStateError') {
        setError(t('webauthn.alreadyRegistered'));
      } else {
        setError(e.message || t('webauthn.registrationError'));
      }
    } finally {
      setRegistering(false);
    }
  };

  // Authenticate with a security key
  const authenticateWithKey = async (keyId) => {
    setAuthenticating(true);
    setError(null);

    try {
      const challenge = generateRandomBytes(32);

      const publicKeyCredentialRequestOptions = {
        challenge: challenge,
        rpId: window.location.hostname,
        allowCredentials: keyId ? [{
          id: base64URLToArrayBuffer(keyId),
          type: 'public-key',
          transports: ['usb', 'nfc', 'ble', 'internal']
        }] : securityKeys.map(key => ({
          id: base64URLToArrayBuffer(key.id),
          type: 'public-key',
          transports: ['usb', 'nfc', 'ble', 'internal']
        })),
        userVerification: 'preferred',
        timeout: 60000
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      if (assertion) {
        setSuccess(t('webauthn.authSuccess'));
        setTimeout(() => setSuccess(null), 3000);
        
        // Update last used
        const updatedKeys = securityKeys.map(key => 
          key.id === arrayBufferToBase64URL(assertion.rawId)
            ? { ...key, lastUsed: new Date().toISOString() }
            : key
        );
        saveKeys(updatedKeys);
        
        return true;
      }
    } catch (e) {
      console.error('WebAuthn authentication error:', e);
      if (e.name === 'NotAllowedError') {
        setError(t('webauthn.authCancelled'));
      } else {
        setError(e.message || t('webauthn.authError'));
      }
      return false;
    } finally {
      setAuthenticating(false);
    }
  };

  // Delete a security key
  const deleteKey = (keyId) => {
    if (window.confirm(t('webauthn.confirmDelete'))) {
      const updatedKeys = securityKeys.filter(k => k.id !== keyId);
      saveKeys(updatedKeys);
    }
  };

  if (!isSupported) {
    return (
      <div className="webauthn-manager">
        <div className="webauthn-header">
          <h2><FiShield size={24} /> {t('webauthn.title')}</h2>
          <button className="btn-icon" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="webauthn-body">
          <div className="webauthn-unsupported">
            <FiAlertCircle size={48} />
            <h3>{t('webauthn.notSupported')}</h3>
            <p>{t('webauthn.notSupportedDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="webauthn-manager">
      <div className="webauthn-header">
        <h2><FiShield size={24} /> {t('webauthn.title')}</h2>
        <button className="btn-icon" onClick={onClose}><FiX size={18} /></button>
      </div>

      <div className="webauthn-body">
        {error && (
          <div className="alert alert-error">
            <FiAlertCircle /> {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <FiCheck /> {success}
          </div>
        )}

        <div className="webauthn-intro">
          <div className="intro-icon">
            <FiKey size={32} />
          </div>
          <div className="intro-text">
            <h3>{t('webauthn.introTitle')}</h3>
            <p>{t('webauthn.introDesc')}</p>
          </div>
        </div>

        <div className="webauthn-actions">
          <button 
            className="btn-primary"
            onClick={() => setShowRegisterModal(true)}
          >
            <FiPlus /> {t('webauthn.addKey')}
          </button>
          
          {securityKeys.length > 0 && (
            <button 
              className="btn-secondary"
              onClick={() => authenticateWithKey()}
              disabled={authenticating}
            >
              <FiLock /> {authenticating ? t('webauthn.authenticating') : t('webauthn.authenticate')}
            </button>
          )}
        </div>

        <div className="security-keys-list">
          <h3>{t('webauthn.registeredKeys')}</h3>
          
          {securityKeys.length === 0 ? (
            <div className="empty-keys">
              <FiShield size={32} />
              <p>{t('webauthn.noKeys')}</p>
            </div>
          ) : (
            securityKeys.map(key => (
              <div key={key.id} className="security-key-item">
                <div className="key-icon">
                  {key.type === 'platform' ? <FiSmartphone size={24} /> : <FiKey size={24} />}
                </div>
                <div className="key-info">
                  <div className="key-name">{key.name}</div>
                  <div className="key-meta">
                    {key.type === 'platform' ? t('webauthn.platformKey') : t('webauthn.securityKey')}
                    {key.lastUsed && ` • ${t('webauthn.lastUsed')}: ${new Date(key.lastUsed).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="key-actions">
                  <button
                    className="btn-icon"
                    onClick={() => authenticateWithKey(key.id)}
                    title={t('webauthn.testKey')}
                  >
                    <FiCheck size={16} />
                  </button>
                  <button
                    className="btn-icon danger"
                    onClick={() => deleteKey(key.id)}
                    title={t('common.delete')}
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="webauthn-info">
          <h4>{t('webauthn.compatibleDevices')}</h4>
          <ul>
            <li>YubiKey 5 Series</li>
            <li>Google Titan Security Key</li>
            <li>Feitian ePass FIDO</li>
            <li>Windows Hello</li>
            <li>Touch ID / Face ID</li>
          </ul>
        </div>
      </div>

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="modal-content webauthn-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('webauthn.registerKey')}</h3>
              <button onClick={() => setShowRegisterModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="register-instructions">
                <div className="instruction-icon">
                  <FiKey size={48} />
                </div>
                <p>{t('webauthn.registerInstructions')}</p>
              </div>

              <div className="form-group">
                <label>{t('webauthn.keyName')}</label>
                <input
                  type="text"
                  placeholder={t('webauthn.keyNamePlaceholder')}
                  value={keyName}
                  onChange={e => setKeyName(e.target.value)}
                  disabled={registering}
                />
              </div>

              <button
                className="btn-primary"
                onClick={registerSecurityKey}
                disabled={registering || !keyName.trim()}
              >
                {registering ? (
                  <>
                    <span className="spinner"></span>
                    {t('webauthn.waitingForKey')}
                  </>
                ) : (
                  <>
                    <FiKey /> {t('webauthn.startRegistration')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebAuthnManager;
